import { FC } from 'react';
import { Box, Paper, Skeleton, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';

import { useAssessmentStats } from '../api';

interface Tile {
  key: string;
  label: string;
  sub: string;
  value: number;
  dot: string;
}

const StatTile: FC<{ tile: Tile; loading: boolean }> = observer(({ tile, loading }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 3.5,
        p: 2,
        backgroundColor: theme.palette.common.white,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875, mb: 1.25 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: tile.dot, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }} noWrap>
          {tile.label}
        </Typography>
      </Box>
      {loading ? (
        <Skeleton variant="text" width={40} height={38} />
      ) : (
        <Typography sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: 'text.primary' }}>
          {tile.value}
        </Typography>
      )}
      <Typography sx={{ fontSize: 11, color: 'text.tertiary', mt: 0.75 }} noWrap>
        {tile.sub}
      </Typography>
    </Paper>
  );
});

/** Four assessment counters (total / completed / processing / drafts). */
export const StatStrip: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const { data, isLoading } = useAssessmentStats();

  const tiles: Tile[] = [
    {
      key: 'total',
      label: t('app.dashboard.stats.total'),
      sub: t('app.dashboard.stats.totalSub'),
      value: data?.total ?? 0,
      dot: theme.palette.text.primary,
    },
    {
      key: 'completed',
      label: t('app.dashboard.stats.completed'),
      sub: t('app.dashboard.stats.completedSub'),
      value: data?.completed ?? 0,
      dot: theme.palette.green[700],
    },
    {
      key: 'processing',
      label: t('app.dashboard.stats.processing'),
      sub: t('app.dashboard.stats.processingSub'),
      value: data?.processing ?? 0,
      dot: theme.palette.blue[700],
    },
    {
      key: 'drafts',
      label: t('app.dashboard.stats.drafts'),
      sub: t('app.dashboard.stats.draftsSub'),
      value: data?.drafts ?? 0,
      dot: theme.palette.grey[500],
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.75,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' },
        mb: 2.25,
      }}
    >
      {tiles.map((tile) => (
        <StatTile key={tile.key} tile={tile} loading={isLoading} />
      ))}
    </Box>
  );
});
